/**
 * The campaign record's typed edge (platform design §5.1, §5.1.1), transcribed field for field
 * from `api`'s `schemas/campaigns.py` and `routers/campaigns.py` (`CampaignDetailOut`,
 * `CandidateOut`, `CampaignUpdateIn`, `PresenceOut`).
 *
 * Creating a campaign is `tenant/home/useCampaigns.ts`'s `useCreateCampaign`
 * (`POST /tenants/{t}/campaigns`, `CampaignCreate` in `api`'s `schemas/commercial.py`), and it
 * carries no candidate.
 */

import { z } from 'zod';

import { platformSchema } from '../../accounts/api/schemas';

export const CAMPAIGN_STATES = ['ACTIVE', 'PAUSED', 'ARCHIVED'] as const;
export const campaignStateSchema = z.enum(CAMPAIGN_STATES);
export type CampaignState = z.infer<typeof campaignStateSchema>;

/** Every field but `name` is nullable: an absent office or party is an empty field, not "".
 *  `tse_election_id`/`tse_registration_id` are the roster row this candidature was linked from
 *  (`api`'s `CandidateOut`, platform design §11.4) -- both null for a hand-typed candidature.
 *  `api` always sends the pair, so absence fails the parse rather than reading as unlinked. */
export const candidateSchema = z.object({
  name: z.string(),
  // A `tse_offices` name (`api` refuses any other), and the number and UF that go with it.
  office: z.string().nullable(),
  ballot_number: z.string().nullable(),
  uf: z.string().nullable(),
  party: z.string().nullable(),
  coalition: z.string().nullable(),
  tse_election_id: z.uuid().nullable(),
  tse_registration_id: z.string().nullable(),
});
export type Candidate = z.infer<typeof candidateSchema>;

export const ELECTION_SCOPES = ['MUNICIPAL', 'STATE', 'FEDERAL'] as const;
export const electionScopeSchema = z.enum(ELECTION_SCOPES);
export type ElectionScope = z.infer<typeof electionScopeSchema>;

/** `api`'s `ElectionSummaryOut`: the election a candidature was linked from, embedded in the
 *  record so its label renders with the rest of the card. */
export const electionSummarySchema = z.object({
  id: z.uuid(),
  cycle_year: z.number().int(),
  scope: electionScopeSchema,
  office: z.string(),
  round: z.number().int(),
  election_date: z.iso.date(),
});
export type ElectionSummary = z.infer<typeof electionSummarySchema>;

/** The read side of `candidateSchema` (`api`'s `CandidateDetailOut`); `election` is null for a
 *  hand-typed candidature. Never sent back: `PUT` takes `candidateSchema`. */
export const candidateDetailSchema = candidateSchema.extend({
  election: electionSummarySchema.nullable(),
  // The TSE photo, signed; null until stored or where TSE publishes none.
  photo_url: z.string().nullable().optional(),
});
export type CandidateDetail = z.infer<typeof candidateDetailSchema>;

/** `platform: null` is the campaign's own site; every other value is a handle on that platform.
 *  `address` is the stored form -- a platform handle with no leading `@` (`api`'s
 *  `normalise_handle` strips it), a site's full `http(s)` URL. */
/** What the latest profile collection read off an account; every figure null when unknown. */
export const profileSchema = z.object({
  display_name: z.string().nullable(),
  bio: z.string().nullable(),
  avatar_url: z.string().nullable(),
  verified: z.boolean().nullable(),
  is_private: z.boolean().nullable(),
  followers: z.number().int().nullable(),
  following: z.number().int().nullable(),
  posts_count: z.number().int().nullable(),
  fetched_at: z.iso.datetime({ offset: true }).nullable(),
});
export type Profile = z.infer<typeof profileSchema>;

export const presenceSchema = z.object({
  id: z.uuid(),
  platform: platformSchema.nullable(),
  address: z.string(),
  // null until a profile collection has read the handle; a site never has one.
  profile: profileSchema.nullable().optional(),
});
export type Presence = z.infer<typeof presenceSchema>;

/** `POST /campaigns/{id}/presences`'s body (`PresenceIn`). */
export interface PresenceWrite {
  platform: z.infer<typeof platformSchema> | null;
  address: string;
}

/** `api`'s `ScopeOut`/`LocalityCountOut` (`schemas/campaigns.py`): the campaign's own electoral
 *  unit and the counts `core`'s `campaign_scope.COUNTED_LEVELS` rolls up under it -- a country
 *  counts its states, a state its municipalities and neighbourhoods, a municipality its
 *  neighbourhoods. `district` never appears here: `COUNTED_LEVELS` never keys or counts it. */
export const CAMPAIGN_SCOPE_LEVELS = ['neighborhood', 'municipality', 'state', 'country'] as const;
export const campaignScopeLevelSchema = z.enum(CAMPAIGN_SCOPE_LEVELS);
export type CampaignScopeLevel = z.infer<typeof campaignScopeLevelSchema>;

export const campaignScopeCountSchema = z.object({
  level: campaignScopeLevelSchema,
  count: z.number().int(),
});
export type CampaignScopeCount = z.infer<typeof campaignScopeCountSchema>;

/** One place above the scope's own unit in the electoral hierarchy (a state above a
 *  municipality, a country above a state). */
export const campaignScopeAncestorSchema = z.object({
  level: campaignScopeLevelSchema,
  name: z.string(),
});
export type CampaignScopeAncestor = z.infer<typeof campaignScopeAncestorSchema>;

export const campaignScopeSchema = z.object({
  // Território's own reference into the place registry (`useCampaignRecord`'s consumer reads
  // this, not the name, to key its map/lookup).
  place_id: z.uuid(),
  // The boundary the territory tiles key by `id`: the map opens on it.
  locality_id: z.uuid(),
  level: campaignScopeLevelSchema,
  name: z.string(),
  counts: z.array(campaignScopeCountSchema),
  // Outermost first (a country before its state before its municipality) -- the breadcrumb
  // order Território renders.
  ancestors: z.array(campaignScopeAncestorSchema),
});
export type CampaignScope = z.infer<typeof campaignScopeSchema>;

/** `api`'s bounds on the two retention periods, in seconds (`socialsrc_core.services.retention`). */
export const MIN_RETENTION_SECONDS = 30 * 86400;
export const MIN_UNRELATED_RETENTION_SECONDS = 6 * 3600;
export const MAX_RETENTION_SECONDS = 3650 * 86400;

export const campaignDetailSchema = z.object({
  id: z.uuid(),
  tenant_id: z.uuid(),
  name: z.string(),
  // Descriptive only (Identificação's "Do que a campanha trata"): nothing reads it to decide
  // anything. `api` always sends the key.
  description: z.string().nullable(),
  timezone: z.string(),
  active_from: z.iso.date().nullable(),
  active_to: z.iso.date().nullable(),
  state: campaignStateSchema,
  // Set exactly when `state` is ARCHIVED (the table's `archived_at_matches_state` CHECK).
  archived_at: z.iso.datetime({ offset: true }).nullable(),
  // When the campaign last entered PAUSED; null in any other state.
  paused_at: z.iso.datetime({ offset: true }).nullable().optional(),
  candidate: candidateDetailSchema.nullable(),
  default_profile_id: z.uuid().nullable(),
  // The night window is read here and written in the tracking editor; all three are null
  // together when the campaign has none, never zero.
  night_start_local: z.iso.time().nullable(),
  night_end_local: z.iso.time().nullable(),
  night_multiplier: z.number().nullable(),
  tracked_accounts: z.number().int(),
  presences: z.array(presenceSchema),
  // null until something has been collected; never 0. `related`/`total` are the same reading's
  // raw counts (`related_post_share == related / total`), null together with the share.
  related_post_share: z.number().nullable(),
  related_post_share_related: z.number().int().nullable(),
  related_post_share_total: z.number().int().nullable(),
  related_post_share_reason: z.string().nullable(),
  // null until a unit is chosen; `api` always sends the key.
  scope: campaignScopeSchema.nullable(),
  // Seconds kept after the campaign ends: the campaign's own override, null inheriting
  // `inherited_retention_seconds`, the organisation's setting.
  data_retention_seconds: z.number().int().nullable(),
  inherited_retention_seconds: z.number().int(),
  // When the campaign's data is deleted; null while it has no end date and is not archived.
  data_purge_at: z.iso.datetime({ offset: true }).nullable(),
  // Seconds a post no live campaign reads as related is kept after it was collected.
  unrelated_media_retention_seconds: z.number().int().nullable(),
  inherited_unrelated_media_retention_seconds: z.number().int(),
});
export type CampaignDetail = z.infer<typeof campaignDetailSchema>;

/**
 * The night window is not editable here: the route refuses it with a 422, so the schema is
 * strict and a payload carrying one is not a `CampaignUpdate`.
 */
export const campaignUpdateSchema = z.strictObject({
  name: z.string(),
  // Required, like `api`'s `CampaignUpdateIn.description`: a body without it is a 422.
  description: z.string().nullable(),
  timezone: z.string(),
  active_from: z.iso.date().nullable(),
  active_to: z.iso.date().nullable(),
  candidate: candidateSchema.nullable(),
  // Each omitted leaves the override as it is; null inherits the organisation's setting.
  data_retention_seconds: z.number().int().nullable().optional(),
  unrelated_media_retention_seconds: z.number().int().nullable().optional(),
});
export type CampaignUpdate = z.infer<typeof campaignUpdateSchema>;

/**
 * The zones the product serves (Período's "Fuso" select, frame `65:2`): Brazil's IANA zones,
 * labelled by the frame's own city + UTC-offset convention ("Maceió (UTC-3)"). `api` validates
 * any IANA id (`_validate_iana_timezone`), so this list only bounds the select's own options --
 * it is not itself a source of truth the server checks against.
 */
export const CAMPAIGN_TIMEZONES: readonly { value: string; label: string }[] = [
  { value: 'America/Noronha', label: 'Fernando de Noronha (UTC-2)' },
  { value: 'America/Belem', label: 'Belém (UTC-3)' },
  { value: 'America/Fortaleza', label: 'Fortaleza (UTC-3)' },
  { value: 'America/Recife', label: 'Recife (UTC-3)' },
  { value: 'America/Maceio', label: 'Maceió (UTC-3)' },
  { value: 'America/Bahia', label: 'Salvador (UTC-3)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
  { value: 'America/Campo_Grande', label: 'Campo Grande (UTC-4)' },
  { value: 'America/Cuiaba', label: 'Cuiabá (UTC-4)' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (UTC-4)' },
  { value: 'America/Boa_Vista', label: 'Boa Vista (UTC-4)' },
  { value: 'America/Manaus', label: 'Manaus (UTC-4)' },
  { value: 'America/Eirunepe', label: 'Eirunepé (UTC-5)' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (UTC-5)' },
];

export const ALLIANCE_RELATIONS = ['ally', 'opponent'] as const;
export const ALLIANCE_KINDS = ['candidate', 'party', 'organization', 'entity'] as const;

/** `api`'s `AllianceOut`: an ally or opponent, a TSE candidature, party or global entity. */
export const allianceSchema = z.object({
  relation: z.enum(ALLIANCE_RELATIONS),
  name: z.string(),
  party: z.string().nullable(),
  number: z.string().nullable(),
  office: z.string().nullable(),
  source: z.enum(['tse', 'manual']),
  kind: z.enum(ALLIANCE_KINDS),
  named_entity_id: z.uuid().nullable(),
  tse_election_id: z.uuid().nullable(),
  tse_registration_id: z.string().nullable(),
  photo_url: z.string().nullable(),
  // Only on a search option: the registered name or the entity's description.
  detail: z.string().nullable().optional(),
});
export type Alliance = z.infer<typeof allianceSchema>;
export type AllianceRelation = Alliance['relation'];

export const alliancesSchema = z.object({ items: z.array(allianceSchema) });

/** `PUT /campaigns/{id}/alliances`'s item (`AllianceIn`): the whole list replaces the stored one. */
export function allianceWrite(alliance: Alliance): Omit<Alliance, 'photo_url' | 'detail'> {
  return {
    relation: alliance.relation,
    name: alliance.name,
    party: alliance.party,
    number: alliance.number,
    office: alliance.office,
    source: alliance.source,
    kind: alliance.kind,
    named_entity_id: alliance.named_entity_id,
    tse_election_id: alliance.tse_election_id,
    tse_registration_id: alliance.tse_registration_id,
  };
}

/** `api`'s `PostRowOut` (`schemas/read/posts.py`), the row shape `UnrelatedPostsOut.rows`
 *  reuses rather than defining a second one. */
export const outOfGroupPostSchema = z.object({
  post_id: z.uuid(),
  account_id: z.uuid(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  platform: z.string(),
  kind: z.string(),
  published_at: z.iso.datetime({ offset: true }),
  url: z.string().nullable(),
  caption: z.string().nullable(),
  deleted_at: z.iso.datetime({ offset: true }).nullable(),
  media: z.array(
    z.object({
      ordinal: z.number().int(),
      media_kind: z.string(),
      source_url: z.string().nullable(),
    }),
  ),
});

export const unrelatedPostsSchema = z.object({
  rows: z.array(outOfGroupPostSchema),
  total: z.number().int(),
});
